import {groupBy, isEmpty, map, orderBy, sortBy, uniqBy} from 'lodash';
import {Fragment, useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {agentActionsApi} from 'api';
import {Button} from 'components';
import {UserContext, UserSessionContext} from 'context';
import {useApiData, useUserSession} from 'hooks';
import {SessionHistory} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale';
import {SidebarView} from 'pages/User/Conference/types';
import {User} from 'types';
import {formatDate} from 'utils/transform';

import {SessionNotesAccordion} from './SessionNotesAccordion';

const T_KEYS = TRANSLATION_KEYS.IN_SESSION_PAGE.NOTES_SIDEBAR;

type Props = {
    localUser: User;
}

export const PrevSessionsNotes: React.FC<Props> = ({localUser}) => {
    const {t} = useTranslation();
    const {settings: {common: {timeZone, dateFormat}}} = useContext(UserContext);

    const [page, setPage] = useState(1);
    const [allSessionHistories, setAllSessionHistories] = useState<SessionHistory[]>([]);

    const groupedByDateSessionHistories = useMemo(() =>
        groupBy(allSessionHistories, (sessionHistory) =>
            formatDate(sessionHistory.startDate, timeZone, dateFormat)), [allSessionHistories, timeZone, dateFormat]);

    const orderedDates = useMemo(() =>
        orderBy(Object.keys(groupedByDateSessionHistories), (date) => new Date(date), 'desc'), [groupedByDateSessionHistories]);

    const {sidebarView} = useContext(UserSessionContext);
    const {sessionParams} = useUserSession();
    const [fetchRequired, setFetchRequired] = useState(true);

    const {
        send: fetchPrevSessionsNotesFn,
        data: prevSessionsNotesData,
        loading: fetchPrevSessionsNotesLoading,
        error: fetchPrevSessionsNotesError
    } = useApiData(agentActionsApi.getPrevSessionsNotes);

    const fetchPrevSessionsNotes = useCallback(() => {
        fetchPrevSessionsNotesFn(sessionParams, page);
        setFetchRequired(false);
    }, [sessionParams, page, fetchPrevSessionsNotesFn]);

    const hasLoadMore = useMemo(() => {
        if (fetchPrevSessionsNotesError) {
            return false;
        }

        if (isEmpty(prevSessionsNotesData)) {
            return true;
        }

        return prevSessionsNotesData.page < prevSessionsNotesData.pages;
    }, [prevSessionsNotesData, fetchPrevSessionsNotesError]);

    const handleLoadMore = useCallback(() => {
        setFetchRequired(true);
        setPage((prev) => ++prev);
    }, []);

    useEffect(() => {
        if (fetchRequired && sidebarView === SidebarView.Notes && page > 0) {
            fetchPrevSessionsNotes();
        }
    }, [fetchPrevSessionsNotes, sidebarView, page, fetchRequired]);

    useEffect(() => {
        if (!isEmpty(prevSessionsNotesData?.items)) {
            setAllSessionHistories((prev) => {
                const uniqueItems = uniqBy([...prev, ...prevSessionsNotesData.items], 'sessionId');
                return sortBy(uniqueItems, (item) => new Date(item.startDate));
            });
        }
    }, [prevSessionsNotesData]);

    return (
        <Fragment>
            {!isEmpty(prevSessionsNotesData?.items) &&
                <div>
                    <div className='tw-ts-h5-bold'>{t(T_KEYS.PREVIOUS_NOTES)}</div>
                    {isEmpty(orderedDates) && <div className='tw-ts-h5 tw-my-4 tw-text-default-dimmed'>{t(T_KEYS.NO_NOTES)}</div>}
                    {map(orderedDates, (date) => (
                        <Fragment key={date}>
                            <div className='tw-text-center tw-my-4'>{date}</div>
                            {map(groupedByDateSessionHistories[date], (sessionHistory) => (
                                <SessionNotesAccordion
                                    key={sessionHistory.sessionId}
                                    session={sessionHistory}
                                    localUserEid={localUser.eid}
                                />
                            ))}
                        </Fragment>
                    ))}
                </div>}
            {hasLoadMore &&
                <div className='tw-text-center'>
                    <Button
                        loading={fetchPrevSessionsNotesLoading}
                        className='tw-my-4'
                        onClick={handleLoadMore}
                    >
                        {t(T_KEYS.LOAD_MORE)}
                    </Button>
                </div>}
        </Fragment>
    );
};